import { useEffect, useState } from "react";
import { Copy, CreditCard, Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, slDateTime, PAYMENT } from "@/lib/api";
import { inputCls } from "@/pages/Login";

const ORDER_STATUS = { neplacano: "Neplačano", caka_nakazilo: "Čaka na plačilo", placano: "Plačano", vrnjeno: "Vrnjeno", preklicano: "Preklicano" };

const copy = (v, label) => { navigator.clipboard.writeText(String(v)); toast.success(`${label} kopiran.`); };

export function UnpaidBanner({ wedding, onPay }) {
  if (wedding.is_demo || wedding.payment_status === "placano") return null;
  const waiting = wedding.payment_status === "caka_nakazilo";
  return (
    <div className="mt-6 p-6 rounded-3xl bg-[#FBEBDD] border border-[#E9C9A8] flex flex-col sm:flex-row sm:items-center gap-5" data-testid="unpaid-banner">
      <Lock className="text-[#8A5A2B] shrink-0" size={28} />
      <div className="flex-1">
        <p className="font-semibold tracking-wide text-[#8A5A2B]" data-testid="unpaid-status-label">{waiting ? "ČAKA NA NAKAZILO" : (PAYMENT[wedding.payment_status] || "Neplačano").toUpperCase()} – Galerija še ni aktivirana</p>
        <p className="text-sm text-[#6B4A2B] mt-1">Vajina galerija je skoraj pripravljena! Za aktivacijo poravnajta naročilo. Do takrat lahko še naprej urejata podatke o vajini poroki.</p>
      </div>
      <button onClick={onPay} className="btn-dark px-6 h-12 text-sm whitespace-nowrap" data-testid="unpaid-pay-button">Aktiviraj galerijo – plačaj naročilo</button>
    </div>
  );
}

function Row({ label, value, copyLabel, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#EADBCE] last:border-0">
      <span className="text-sm text-[#6C6560]">{label}</span>
      <span className="flex items-center gap-2 text-right">
        <b className="text-sm" data-testid={testid}>{value}</b>
        {copyLabel && value && <button onClick={() => copy(value, copyLabel)} className="p-1.5 rounded-lg hover:bg-[#F3ECE1]" aria-label={`Kopiraj ${copyLabel}`}><Copy size={14} /></button>}
      </span>
    </div>
  );
}

export function BankDetails({ bank, order }) {
  if (!order) return null;
  const amount = Number(order.amount).toFixed(2).replace(".", ",");
  return (
    <div className="card-lux p-6 border-2 border-[#C5A880]" data-testid={`bank-details-${order.type}`}>
      <h3 className="font-display text-2xl tracking-wide">PODATKI ZA PLAČILO</h3>
      {!bank.configured && <p className="mt-3 p-3 rounded-xl bg-[#F8E3DE] text-sm text-[#8A3B2B]" data-testid="bank-not-configured">Bančni podatki še niso objavljeni. Prosimo, počakajte ali nam pišite na info@najintrenutek.eu.</p>}
      <div className="mt-4">
        <Row label="Prejemnik" value={bank.recipient} testid="bank-recipient" />
        <Row label="IBAN / TRR" value={bank.iban || "—"} copyLabel={bank.iban ? "IBAN" : null} testid="bank-iban" />
        <Row label="Banka" value={[bank.bank, bank.bic].filter(Boolean).join(" · ") || "—"} testid="bank-name" />
        <Row label="Znesek" value={`${amount} €`} testid="bank-amount" />
        <Row label="Namen plačila" value={order.purpose} testid="bank-purpose" />
        <Row label="Referenca" value={order.reference} testid="bank-reference" />
        <Row label="Status plačila" value={ORDER_STATUS[order.status]} testid="bank-status" />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button disabled={!bank.iban} onClick={() => copy(bank.iban.replace(/\s/g, ""), "IBAN")} className="btn-line px-4 py-2 text-sm disabled:opacity-40" data-testid="copy-iban-button">Kopiraj IBAN</button>
        <button onClick={() => copy(order.reference, "Referenca")} className="btn-line px-4 py-2 text-sm" data-testid="copy-reference-button">Kopiraj referenco</button>
        <button onClick={() => copy(amount, "Znesek")} className="btn-line px-4 py-2 text-sm" data-testid="copy-amount-button">Kopiraj znesek</button>
      </div>
      <p className="text-sm text-[#6C6560] mt-4">Za aktivacijo galerije lahko naročilo poravnata z nakazilom na naš TRR. Pri plačilu obvezno navedita prikazano referenco, da bomo lahko prepoznali vajino nakazilo.</p>
    </div>
  );
}

function PackagePicker({ wedding, billing, onChange }) {
  const [pkgs, setPkgs] = useState([]);
  const [pid, setPid] = useState(billing.package_id);
  const [tables, setTables] = useState(billing.table_count || 10);
  useEffect(() => { api.get("/public/packages").then((r) => setPkgs(r.data)); }, []);
  const pkg = pkgs.find((p) => p.id === pid);
  const save = async () => {
    try { const r = await api.patch(`/manage/weddings/${wedding.id}/package`, { package_id: pid, table_count: Number(tables) || 0 }); onChange(r.data); toast.success("Paket je posodobljen."); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lux p-6 space-y-4" data-testid="package-picker">
      <h3 className="font-display text-2xl">Paket</h3>
      <div className="grid sm:grid-cols-3 gap-2">
        {pkgs.map((p) => (
          <button key={p.id} type="button" onClick={() => setPid(p.id)} className={`p-4 rounded-2xl border text-left ${pid === p.id ? "border-[#231F20] bg-white" : "border-[#DFD5C5]"}`} data-testid={`billing-package-${p.id}`}>
            <span className="block font-display text-xl">{p.name}</span>
            <span className="text-sm text-[#6C6560]">{p.price} €{p.per_table_price ? ` + ${p.per_table_price} €/miza` : ""}</span>
          </button>
        ))}
      </div>
      {pkg?.per_table_price > 0 && (
        <label className="block text-sm text-[#6C6560]">Število miz (namizne kartice z QR kodo)
          <input type="number" min="1" max="80" className={`${inputCls} mt-1`} value={tables} onChange={(e) => setTables(e.target.value)} data-testid="billing-tables-input" /></label>
      )}
      <button onClick={save} className="btn-line px-5 h-11 text-sm" data-testid="billing-package-save">Shrani paket</button>
    </div>
  );
}

export default function PaymentPanel({ wedding, onPaid }) {
  const [b, setB] = useState(null);
  const load = () => api.get(`/manage/weddings/${wedding.id}/billing`).then((r) => setB(r.data));
  useEffect(() => { load(); }, [wedding.id, wedding.payment_status]);
  if (!b) return null;
  const paid = b.payment_status === "placano";
  const pkgOrder = b.orders.find((o) => o.type === "package" && ["neplacano", "caka_nakazilo"].includes(o.status));
  const chooseBank = async () => {
    try { const r = await api.post(`/manage/weddings/${wedding.id}/pay`, { type: "package" }); setB(r.data); onPaid && onPaid(); toast.success("Podatki za nakazilo so pripravljeni."); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="payment-panel">
      <div className="card-lux p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Povzetek naročila</p>
          <p className="font-display text-3xl mt-2" data-testid="billing-order-total">{Number(b.order_total || 0).toFixed(2).replace(".", ",")} €</p>
          <p className="text-sm text-[#6C6560]">{wedding.package_name}{b.table_count ? ` · ${b.table_count} miz` : ""}</p>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${paid ? "bg-[#E7EFE1] text-[#4E6B3D]" : "bg-[#FBEBDD] text-[#8A5A2B]"}`} data-testid="billing-payment-status">{paid ? "PLAČANO" : (PAYMENT[b.payment_status] || "Neplačano").toUpperCase()}</span>
      </div>
      {b.editable && <PackagePicker wedding={wedding} billing={b} onChange={(d) => { setB(d); onPaid && onPaid(); }} />}
      {!paid && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="payment-methods">
          <div className="card-lux p-6 opacity-60" data-testid="payment-method-card">
            <CreditCard className="text-[#B39264]" /><h4 className="font-medium mt-3">A – Plačilo s kartico</h4>
            <p className="text-sm text-[#6C6560] mt-1">Spletno plačilo s kartico bo na voljo kmalu.</p>
          </div>
          <button onClick={chooseBank} className={`card-lux p-6 text-left ${pkgOrder?.status === "caka_nakazilo" ? "ring-2 ring-[#231F20]" : ""}`} data-testid="payment-method-bank">
            <Landmark className="text-[#B39264]" /><h4 className="font-medium mt-3">B – Bančno nakazilo</h4>
            <p className="text-sm text-[#6C6560] mt-1">Prika\u017eemo IBAN in unikatno referenco. Galerijo aktiviramo, ko potrdimo prejem nakazila.</p>
          </button>
        </div>
      )}
      {!paid && pkgOrder?.status === "caka_nakazilo" && <BankDetails bank={b.bank} order={pkgOrder} />}
      <div className="card-lux overflow-x-auto" data-testid="order-history">
        <h3 className="font-display text-2xl p-5">Zgodovina naročil</h3>
        <table className="w-full text-sm">
          <thead className="bg-[#F3ECE1] text-left"><tr><th className="p-3">Številka</th><th className="p-3">Naročilo</th><th className="p-3">Znesek</th><th className="p-3">Status</th><th className="p-3">Datum</th></tr></thead>
          <tbody>
            {b.orders.map((o) => <tr key={o.id} className="border-t border-[#EADBCE]"><td className="p-3">{o.number}</td><td className="p-3">{o.label}</td><td className="p-3">{o.amount} €</td><td className="p-3">{ORDER_STATUS[o.status]}</td><td className="p-3 text-[#6C6560]">{slDateTime(o.confirmed_at || o.created_at)}</td></tr>)}
            {b.orders.length === 0 && <tr><td colSpan={5} className="p-4 text-[#6C6560]">Ni naročil.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
